// Code-action: "Add missing table to FROM" — FK edge collection
// (bidirectional walk).
//
// Split out of addMissingTableToFrom.ts (behavior-neutral). The
// schema-permissive FK matching (`findForeignKeysOnTable` honoring
// `ScopeRelation.userTypedSchema`) and the strict ordering (the orchestrator
// computes in-scope relations BEFORE passing to `collectFkEdges`) are
// preserved exactly — they flow through function arguments.

import type { DatabaseExplorerNode } from '../../../explorer/models/DatabaseTreeModels';
import type {
  Candidate,
  FkOption,
  FkPair,
  ScopeRelation,
} from './addMissingTableToFrom.types';
import { findTableByName } from './addMissingTableToFrom.tree';
import type { DatabaseEngineId } from '@/core/engines';
import {
  findForeignKeyPairsInSnapshot,
  mergeForeignKeyResults,
  requestForeignKeyLoad,
} from './foreignKeySource';

export function collectFkEdges(
  treeData: readonly DatabaseExplorerNode[],
  engineId: DatabaseEngineId,
  connectionId: string,
  database: string | null,
  candidate: Candidate,
  inScope: readonly ScopeRelation[],
  defaultSchema: string,
): readonly FkOption[] {
  // FKs come from the OE tree AND the language service's snapshot (see
  // `foreignKeySource.ts`) — never only from what the user expanded. A cold
  // snapshot kicks the engine's schema load for the next compute.
  requestForeignKeyLoad(engineId, connectionId, database);
  const out: FkOption[] = [];
  for (const other of inScope) {
    // Direction A: FK on the candidate, references `other`. The "remote" side
    // is the in-scope relation, so the schema-permissive guard uses
    // `other.userTypedSchema` (undefined when unqualified → accept any FK
    // referencedSchema).
    const fksOnCand = findForeignKeysOnTable(
      treeData,
      connectionId,
      database,
      candidate.tableName,
      candidate.schema,
      other.name,
      other.userTypedSchema,
      defaultSchema,
    );
    for (const pairs of fksOnCand) out.push({ fkSide: 'candidate', other, pairs });

    // Direction B: FK on `other`, references the candidate. The "remote" side
    // is the candidate, whose schema is OE-derived (authoritative) — use it
    // for a strict match.
    const fksOnOther = findForeignKeysOnTable(
      treeData,
      connectionId,
      database,
      other.name,
      other.schema,
      candidate.tableName,
      candidate.schema,
      defaultSchema,
    );
    for (const pairs of fksOnOther) out.push({ fkSide: 'in-scope', other, pairs });
  }
  return out;
}

function findForeignKeysOnTable(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  database: string | null,
  localName: string,
  localSchema: string,
  remoteName: string,
  /** Lower-cased schema to enforce the FK referencedSchema match against, or
   *  `undefined` to accept any referencedSchema. Slice 12 pattern — see
   *  `addMissingJoinPredicate.ts:findForeignKeys`. Direction-A callers pass
   *  `inScope.userTypedSchema` (undefined when unqualified); Direction-B
   *  callers pass the candidate's OE-derived schema. */
  remoteSchemaForMatch: string | undefined,
  defaultSchema: string,
): readonly (readonly FkPair[])[] {
  const fromSnapshot = findForeignKeyPairsInSnapshot({
    connectionId,
    database,
    localName,
    localSchemaForMatch: localSchema ? localSchema.toLowerCase() : undefined,
    remoteName,
    remoteSchemaForMatch,
    defaultSchema,
  });
  const tableNode = findTableByName(treeData, connectionId, database, localName, defaultSchema, localSchema)?.node;
  const keysFolder = tableNode ? (tableNode.children ?? []).find((c) => c.type === 'keys-folder') : undefined;
  if (!keysFolder) return fromSnapshot;

  const out: (readonly FkPair[])[] = [];
  for (const keyNode of keysFolder.children ?? []) {
    if (keyNode.type !== 'key') continue;
    const meta = keyNode.metadata as
      | {
          keyType?: 'PRIMARY KEY' | 'FOREIGN KEY' | 'UNIQUE';
          columns?: readonly {
            name: string;
            referencedSchema?: string;
            referencedTable?: string;
            referencedColumn?: string;
          }[];
        }
      | undefined;
    if (meta?.keyType !== 'FOREIGN KEY') continue;
    const cols = meta.columns ?? [];
    if (cols.length === 0) continue;

    const refTable = cols[0].referencedTable;
    const refSchema = cols[0].referencedSchema;
    if (!refTable || refTable.toLowerCase() !== remoteName.toLowerCase()) continue;
    if (
      remoteSchemaForMatch
      && refSchema
      && refSchema.toLowerCase() !== remoteSchemaForMatch
      && refSchema.toLowerCase() !== defaultSchema
    ) continue;

    const pairs: FkPair[] = [];
    for (const c of cols) {
      if (!c.referencedColumn) continue;
      pairs.push({ localCol: c.name, remoteCol: c.referencedColumn });
    }
    if (pairs.length === 0) continue;
    out.push(pairs);
  }
  return mergeForeignKeyResults(out, fromSnapshot);
}
